import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { brandingService, Branding } from '../services/brandingService';
import { tokenService } from '../services/tokenService';

interface BrandingContextType {
    branding: Branding;
    companyName: string;
    loading: boolean;
    loadBrandingByDomain: () => Promise<void>;
    loadBrandingByCompanyId: (companyId: number, token: string) => Promise<void>;
    clearBranding: () => void;
}

const defaultBranding: Branding = {
    logoUrl: null,
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    faviconUrl: null,
    loginBackgroundUrl: null,
    domain: null,
    appTitle: null,
    poweredByText: null,
};

const BrandingContext = createContext<BrandingContextType>({
    branding: defaultBranding,
    companyName: 'Online Academy',
    loading: false,
    loadBrandingByDomain: async () => {},
    loadBrandingByCompanyId: async () => {},
    clearBranding: () => {},
});

export const useBranding = () => useContext(BrandingContext);

interface BrandingProviderProps {
    children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
    const [branding, setBranding] = useState<Branding>(brandingService.getCachedBranding());
    const [companyName, setCompanyName] = useState<string>(brandingService.getCachedCompanyName());
    const [loading, setLoading] = useState(false);

    // Auto-load branding on mount (token-based or domain-based).
    // `tokenService.hasValidToken()` prüft sowohl Existenz als auch
    // Ablauf — ohne diese Prüfung feuerten wir den Token-Call auch mit
    // abgelaufenen/fehlenden Tokens, was im Audit als "Failed to fetch"
    // auflief (CORS-Preflight gegen self-signed HTTPS-API).
    useEffect(() => {
        const initBranding = async () => {
            if (tokenService.hasValidToken()) {
                const token = localStorage.getItem('token')!;
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    if (payload.companyId) {
                        const response = await brandingService.getBrandingByCompanyId(payload.companyId, token);
                        setBranding(response.branding);
                        setCompanyName(response.companyName);
                        return;
                    }
                } catch {
                    // fällt durch zum Domain-Fallback
                }
            }
            const response = await brandingService.getBrandingByDomain();
            setBranding(response.branding);
            setCompanyName(response.companyName);
        };
        initBranding();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update favicon when branding changes
    useEffect(() => {
        if (branding.faviconUrl) {
            brandingService.updateFavicon(branding.faviconUrl);
        }
    }, [branding.faviconUrl]);

    // Override document.title — React Admin resets it on every route change,
    // so we use a MutationObserver to always enforce the branding title.
    useEffect(() => {
        const appName = branding.appTitle || companyName || 'Online Academy';

        const applyTitle = () => {
            if (document.title && !document.title.includes(appName)) {
                const current = document.title;
                const dashIdx = current.lastIndexOf(' - ');
                document.title = dashIdx > 0 ? `${current.substring(0, dashIdx)} - ${appName}` : appName;
            }
        };

        applyTitle();

        const titleEl = document.querySelector('title');
        if (!titleEl) return;

        const observer = new MutationObserver(() => applyTitle());
        observer.observe(titleEl, { childList: true, characterData: true, subtree: true });

        return () => observer.disconnect();
    }, [branding.appTitle, companyName]);

    const loadBrandingByDomain = async () => {
        setLoading(true);
        try {
            const response = await brandingService.getBrandingByDomain();
            setBranding(response.branding);
            setCompanyName(response.companyName);
        } finally {
            setLoading(false);
        }
    };

    const loadBrandingByCompanyId = async (companyId: number, token: string) => {
        setLoading(true);
        try {
            const response = await brandingService.getBrandingByCompanyId(companyId, token);
            setBranding(response.branding);
            setCompanyName(response.companyName);
        } finally {
            setLoading(false);
        }
    };

    const clearBranding = () => {
        brandingService.clearCache();
        setBranding(defaultBranding);
        setCompanyName('Online Academy');
    };

    return (
        <BrandingContext.Provider
            value={{
                branding,
                companyName,
                loading,
                loadBrandingByDomain,
                loadBrandingByCompanyId,
                clearBranding,
            }}
        >
            {children}
        </BrandingContext.Provider>
    );
};

export default BrandingContext;
